import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { useAuth } from "../../../AuthProvider/AuthContext";
import Login from "../../Main/Login/Login";
import Signup from "../../Main/Signup/Signup";
import icelandAttraction from "../../../assets/hero-top/activities-1.png";
import czechAttraction from "../../../assets/Places/london.png";
import turkeyAttraction from "../../../assets/holidays/thailand.png";
import hungaryAttraction from "../../../assets/Places/paris.png";
import dubaiAttraction from "../../../assets/Places/dubai.png";
import singaporeAttraction from "../../../assets/Places/singapore.png";
import japanAttraction from "../../../assets/Places/tokyo.png";
import malaysiaAttraction from "../../../assets/Places/lumpur.png";
import "./Visa.css";
import usePageMeta from "../../../hooks/usePageMeta";
import {
  BadgeDollarSign, Headset, Lock, ShieldCheck,
  Sparkles, Zap, Globe, ChevronRight, ChevronLeft,
  Upload, X, Check, AlertCircle, Clock, MapPin,
  FileText, User, Plane, Search, Star, ArrowRight, CalendarCheck, ClipboardCheck
} from "lucide-react";

const API_BASE =
  (process.env.REACT_APP_API_URL || "https://makebeetrip.com").replace(/\/$/, "") + "/api";

const authHeaders = (token) => ({
  "Content-Type": "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

/* ─── DATA ─────────────────────────────────────────────────────────────────── */
export const COUNTRIES = [
  { code: "CZ", name: "Czech Republic",       flag: "🇨🇿", processingTime: "10–12 days", fee: 9260,  type: "Schengen",     popular: true  },
  { code: "HU", name: "Hungary",              flag: "🇭🇺", processingTime: "10–12 days", fee: 8500,  type: "Schengen",     popular: true  },
  { code: "IS", name: "Iceland",              flag: "🇮🇸", processingTime: "10–12 days", fee: 8900,  type: "Schengen",     popular: true  },
  { code: "AE", name: "United Arab Emirates", flag: "🇦🇪", processingTime: "3–5 days",   fee: 4500,  type: "E-Visa",       popular: true  },
  { code: "SG", name: "Singapore",            flag: "🇸🇬", processingTime: "5–7 days",   fee: 3200,  type: "E-Visa",       popular: true  },
  { code: "TH", name: "Thailand",             flag: "🇹🇭", processingTime: "2–4 days",   fee: 2800,  type: "E-Visa",       popular: true  },
  { code: "US", name: "United States",        flag: "🇺🇸", processingTime: "15–20 days", fee: 12000, type: "Sticker Visa", popular: true  },
  { code: "GB", name: "United Kingdom",       flag: "🇬🇧", processingTime: "10–15 days", fee: 9500,  type: "Sticker Visa", popular: true  },
  { code: "FR", name: "France",               flag: "🇫🇷", processingTime: "10–12 days", fee: 7200,  type: "Schengen",     popular: true  },
  { code: "DE", name: "Germany",              flag: "🇩🇪", processingTime: "10–12 days", fee: 7200,  type: "Schengen",     popular: false },
  { code: "IT", name: "Italy",                flag: "🇮🇹", processingTime: "10–12 days", fee: 7200,  type: "Schengen",     popular: false },
  { code: "AU", name: "Australia",            flag: "🇦🇺", processingTime: "20–30 days", fee: 11000, type: "E-Visa",       popular: true  },
  { code: "CA", name: "Canada",               flag: "🇨🇦", processingTime: "15–25 days", fee: 10500, type: "Sticker Visa", popular: false },
  { code: "JP", name: "Japan",                flag: "🇯🇵", processingTime: "5–7 days",   fee: 5500,  type: "Sticker Visa", popular: true  },
  { code: "MY", name: "Malaysia",             flag: "🇲🇾", processingTime: "1–3 days",   fee: 1500,  type: "E-Visa",       popular: false },
  { code: "ID", name: "Indonesia / Bali",     flag: "🇮🇩", processingTime: "2–4 days",   fee: 2200,  type: "VOA",          popular: false },
  { code: "NZ", name: "New Zealand",          flag: "🇳🇿", processingTime: "10–15 days", fee: 9000,  type: "E-Visa",       popular: false },
  { code: "ZA", name: "South Africa",         flag: "🇿🇦", processingTime: "7–10 days",  fee: 3800,  type: "Sticker Visa", popular: false },
  { code: "MV", name: "Maldives",             flag: "🇲🇻", processingTime: "On Arrival", fee: 0,     type: "VOA",          popular: false },
  { code: "NL", name: "Netherlands",          flag: "🇳🇱", processingTime: "10–12 days", fee: 7200,  type: "Schengen",     popular: false },
  { code: "CH", name: "Switzerland",          flag: "🇨🇭", processingTime: "10–12 days", fee: 7200,  type: "Schengen",     popular: false },
  { code: "TR", name: "Turkey",               flag: "🇹🇷", processingTime: "1–2 days",   fee: 2900,  type: "E-Visa",       popular: false },
  { code: "EG", name: "Egypt",                flag: "🇪🇬", processingTime: "2–3 days",   fee: 2100,  type: "E-Visa",       popular: false },
];

export const VISA_TYPES = ["Tourist", "Business", "Student", "Medical", "Transit", "Work"];

const STEPS = [
  { id: 1, label: "Destination", icon: <Globe size={16} /> },
  { id: 2, label: "Visa Type",   icon: <FileText size={16} /> },
  { id: 3, label: "Personal",    icon: <User size={16} /> },
  { id: 4, label: "Travel",      icon: <Plane size={16} /> },
  { id: 5, label: "Documents",   icon: <Upload size={16} /> },
  { id: 6, label: "Review",      icon: <Star size={16} /> },
];

const TRENDING_DESTINATIONS = [
  {
    country: "Iceland",
    code: "IS",
    image: icelandAttraction,
  },
  {
    country: "Czech Republic",
    code: "CZ",
    image: czechAttraction,
  },
  {
    country: "Turkey",
    code: "TR",
    image: turkeyAttraction,
  },
  {
    country: "Hungary",
    code: "HU",
    image: hungaryAttraction,
  },
  {
    country: "Dubai",
    code: "AE",
    image: dubaiAttraction,
  },
  {
    country: "Singapore",
    code: "SG",
    image: singaporeAttraction,
  },
  {
    country: "Japan",
    code: "JP",
    image: japanAttraction,
  },
  {
    country: "Malaysia",
    code: "MY",
    image: malaysiaAttraction,
  },
];

const HOW_IT_WORKS = [
  {
    step: "STEP 1",
    number: "01",
    title: "Choose Your Destination",
    text:
      "Select the country you plan to visit and explore the visa options available for your destination.",
    icon: Globe,
  },
  {
    step: "STEP 2",
    number: "02",
    title: "Choose Visa Type",
    text:
      "Select the visa category that matches your purpose of travel, such as tourism, business or another eligible purpose.",
    icon: FileText,
  },
  {
    step: "STEP 3",
    number: "03",
    title: "Share Your Travel Plans",
    text:
      "Provide your traveller details, planned travel date and any additional information about your trip.",
    icon: ClipboardCheck,
  },
  {
    step: "STEP 4",
    number: "04",
    title: "Get Visa Assistance",
    text:
      "Submit your enquiry and our visa assistance team will review your details and contact you with the relevant next steps.",
    icon: Headset,
  },
];

const KNOWN_FOR = [
  {
    title: "Wide Destination Coverage",
    text: "Visa assistance for multiple destinations.",
  },
  {
    title: "Clear Process",
    text: "Simple enquiry with clear next steps.",
  },
  {
    title: "Expert Guidance",
    text: "Guidance based on your visa requirements.",
  },
  {
    title: "Secure Enquiry",
    text: "Your enquiry details are handled securely.",
  },
];

const DOCS_REQUIRED = {
  Tourist:  ["Valid Passport (6 months validity)", "Recent Passport Photos (2×2)", "Bank Statement (last 3 months)", "Flight Itinerary", "Hotel Booking Confirmation", "Travel Insurance"],
  Business: ["Valid Passport (6 months validity)", "Business Invitation Letter", "Company Registration Certificate", "Bank Statement (last 6 months)", "Cover Letter from Employer", "Travel Insurance"],
  Student:  ["Valid Passport (6 months validity)", "University Admission Letter", "Financial Sponsorship Proof", "Academic Transcripts", "Language Proficiency Certificate", "Medical Certificate"],
  Medical:  ["Valid Passport (6 months validity)", "Medical Certificate from Doctor", "Hospital Appointment Letter", "Financial Proof", "Insurance Documents", "Sponsor Letter"],
  Transit:  ["Valid Passport (6 months validity)", "Onward Travel Ticket", "Destination Country Visa", "Bank Statement"],
  Work:     ["Valid Passport (6 months validity)", "Employment Contract", "Work Permit / NOC", "Educational Certificates", "Medical Fitness Certificate", "Police Clearance Certificate"],
};

const fmt = (n) => n === 0 ? "Free" : `₹${Number(n).toLocaleString("en-IN")}`;

/* ─── DB CALLS ──────────────────────────────────────────────────────────────── */
async function saveApplicationToDB(data, step, token) {
  try {
    const payload = {
      step, timestamp: new Date().toISOString(),
      country:  data.country  ? { code: data.country.code, name: data.country.name, flag: data.country.flag, fee: data.country.fee, type: data.country.type, processingTime: data.country.processingTime } : null,
      visaType: data.visaType || null,
      personal: data.personal || {},
      travel:   data.travel   || {},
    };
    const res  = await fetch(`${API_BASE}/visa-applications`, { method: "POST", headers: authHeaders(token), body: JSON.stringify(payload) });
    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    return { success: true, applicationId: json.applicationId || json._id || json.id };
  } catch (err) { return { success: false, error: err.message }; }
}

async function updateApplicationInDB(applicationId, data, step, token) {
  try {
    const payload = {
      step, timestamp: new Date().toISOString(),
      country:  data.country  ? { code: data.country.code, name: data.country.name, flag: data.country.flag, fee: data.country.fee, type: data.country.type, processingTime: data.country.processingTime } : null,
      visaType: data.visaType || null,
      personal: data.personal || {},
      travel:   data.travel   || {},
    };
    const res = await fetch(`${API_BASE}/visa-applications/${applicationId}`, { method: "PUT", headers: authHeaders(token), body: JSON.stringify(payload) });
    if (!res.ok) throw new Error(`${res.status}`);
    return { success: true };
  } catch (err) { return { success: false, error: err.message }; }
}

async function uploadDocumentToDB(applicationId, docName, file, token) {
  try {
    const formData = new FormData();
    formData.append("applicationId", applicationId);
    formData.append("documentName", docName);
    formData.append("file", file);
    const res = await fetch(`${API_BASE}/visa-applications/${applicationId}/documents`, { method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: formData });
    if (!res.ok) throw new Error(`${res.status}`);
    return { success: true };
  } catch (err) { return { success: false, error: err.message }; }
}

async function submitApplicationToDB(applicationId, data, token) {
  try {
    const res = await fetch(`${API_BASE}/visa-applications/${applicationId}/submit`, { method: "POST", headers: authHeaders(token), body: JSON.stringify({ status: "submitted", submittedAt: new Date().toISOString() }) });
    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    return { success: true, referenceNumber: json.referenceNumber || `MBT-${Math.floor(Math.random() * 900000 + 100000)}` };
  } catch (err) { return { success: false, error: err.message }; }
}

/* ─── SAVE INDICATOR ────────────────────────────────────────────────────────── */
function SaveIndicator({ status }) {
  if (!status) return null;
  const config = {
    saving: { label: "Saving…",    cls: "saving" },
    saved:  { label: "Saved",      cls: "saved"  },
    error:  { label: "Save failed", cls: "error" },
  };
  const c = config[status];
  return (
    <span className={`save-pill ${c.cls}`}>
      {status === "saved"  && <Check size={12} />}
      {status === "error"  && <AlertCircle size={12} />}
      {status === "saving" && <span className="spin-dot" />}
      {c.label}
    </span>
  );
}

/* ─── STEP INDICATOR ────────────────────────────────────────────────────────── */
function StepIndicator({ currentStep }) {
  return (
    <nav className="step-nav" aria-label="Application progress">
      <div className="step-nav-inner">
        {STEPS.map((step, i) => {
          const done   = currentStep > step.id;
          const active = currentStep === step.id;
          return (
            <div className="step-item" key={step.id} aria-current={active ? "step" : undefined}>
              <div className={`step-node ${done ? "done" : active ? "active" : "idle"}`}>
                {done ? <Check size={14} strokeWidth={2.5} /> : step.icon}
              </div>
              <span className={`step-label ${active ? "active" : done ? "done" : ""}`}>{step.label}</span>
              {i < STEPS.length - 1 && (
                <div className="step-line-wrap">
                  <div className={`step-line ${done ? "done" : ""}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="step-progress">
        <div className="step-progress-fill" style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }} />
      </div>
    </nav>
  );
}

/* ─── COUNTRY CARD ──────────────────────────────────────────────────────────── */
function CountryCard({ country, selected, onSelect }) {
  const typeColor = {
    "E-Visa": "badge-evisa",
    "Schengen": "badge-schengen",
    "Sticker Visa": "badge-sticker",
    "VOA": "badge-voa",
  };
  return (
    <button
      type="button"
      className={`visa-country-card ${selected ? "selected" : ""}`}
      onClick={() => onSelect(country)}
      aria-pressed={selected}
    >
      {selected && <span className="visa-country-card-check"><Check size={12} strokeWidth={3} /></span>}
      <span className="visa-country-flag">{country.flag}</span>
      <div className="visa-country-info">
        <span className="visa-country-name">{country.name}</span>
        <div className="visa-country-meta">
          <span className={`type-badge ${typeColor[country.type] || ""}`}>{country.type}</span>
          <span className="visa-country-time"><Clock size={11} />{country.processingTime}</span>
        </div>
        <span className="visa-country-fee">{fmt(country.fee)}</span>
      </div>
    </button>
  );
}

/* ─── STEP 1 — DESTINATION ──────────────────────────────────────────────────── */
function Step1({ data, onChange, onCountrySelect }) {
  const [search, setSearch]   = useState("");
  const [showAll, setShowAll] = useState(false);

  const filtered = search
    ? COUNTRIES.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : showAll ? COUNTRIES : COUNTRIES.filter(c => c.popular);

  return (
    <section className="step-panel">
      <header className="panel-header">
        <span className="panel-step-badge">Step 1 of 6</span>
        <h2 className="panel-title">Where are you headed?</h2>
        <p className="panel-subtitle">Select your destination to see visa requirements and fees</p>
      </header>

      <div className="search-field">
        <Search size={16} className="search-ico" />
        <input
          className="search-input"
          type="text"
          placeholder="Search destination…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search countries"
        />
        {search && (
          <button className="search-clear" onClick={() => setSearch("")} aria-label="Clear search">
            <X size={14} />
          </button>
        )}
      </div>

      {!search && (
        <div className="section-heading">
          <span>{showAll ? "All Destinations" : "Popular Destinations"}</span>
          <span className="section-count">{showAll ? COUNTRIES.length : COUNTRIES.filter(c => c.popular).length}</span>
        </div>
      )}

      <div className="countries-grid">
        {filtered.map(c => (
          <CountryCard
            key={c.code}
            country={c}
            selected={data.country?.code === c.code}
            onSelect={c => {
              onChange({ country: c });
              onCountrySelect?.(c);
            }}
          />
        ))}
      </div>

      {!search && (
        <button className="toggle-all-btn" onClick={() => setShowAll(s => !s)}>
          {showAll
            ? <><ChevronLeft size={15} /> Show popular only</>
            : <>View all {COUNTRIES.length} destinations <ChevronRight size={15} /></>}
        </button>
      )}

      {data.country && (
        <div className="selected-dest-banner">
          <span className="dest-flag-lg">{data.country.flag}</span>
          <div className="dest-details">
            <span className="dest-name">{data.country.name}</span>
            <div className="dest-chips">
              <span className="dest-chip">{data.country.type}</span>
              <span className="dest-chip"><Clock size={11} /> {data.country.processingTime}</span>
              <span className="dest-chip highlight">{fmt(data.country.fee)}</span>
            </div>
          </div>
          <Check size={20} className="dest-check-icon" />
        </div>
      )}
    </section>
  );
}

/* ─── STEP 2 — VISA TYPE ────────────────────────────────────────────────────── */
function Step2({ data, onChange }) {
  const meta = {
    Tourist:  { icon: "🏖️", desc: "Leisure, sightseeing, family visits",      duration: "30–90 days",      color: "type-teal"   },
    Business: { icon: "💼", desc: "Conferences, meetings, negotiations",        duration: "30–90 days",      color: "type-blue"   },
    Student:  { icon: "🎓", desc: "Academic programs, language schools",        duration: "Course duration", color: "type-purple" },
    Medical:  { icon: "🏥", desc: "Treatment, surgery, consultations",          duration: "As required",     color: "type-rose"   },
    Transit:  { icon: "🔄", desc: "Passing through to final destination",       duration: "24–96 hours",     color: "type-amber"  },
    Work:     { icon: "🛠️", desc: "Employment and professional assignments",    duration: "1–3 years",       color: "type-green"  },
  };

  return (
    <section className="step-panel">
      <header className="panel-header">
        <span className="panel-step-badge">Step 2 of 6</span>
        <h2 className="panel-title">Purpose of travel</h2>
        <p className="panel-subtitle">Choose the visa category that best matches your travel intent</p>
      </header>

      <div className="visa-type-grid">
        {VISA_TYPES.map(type => {
          const m   = meta[type];
          const sel = data.visaType === type;
          return (
            <button
              key={type}
              type="button"
              className={`visa-type-card ${sel ? "selected" : ""} ${m.color}`}
              onClick={() => onChange({ visaType: type })}
              aria-pressed={sel}
            >
              {sel && <span className="type-check"><Check size={12} strokeWidth={3} /></span>}
              <span className="type-emoji">{m.icon}</span>
              <strong className="type-name">{type}</strong>
              <span className="type-desc">{m.desc}</span>
              <span className="type-duration"><Clock size={11} /> {m.duration}</span>
            </button>
          );
        })}
      </div>

      {data.visaType && (
        <div className="docs-needed">
          <div className="docs-needed-head">
            <FileText size={15} />
            <span>Required documents — <strong>{data.visaType}</strong> visa to {data.country?.name}</span>
          </div>
          <ul className="docs-needed-list">
            {(DOCS_REQUIRED[data.visaType] || []).map((doc, i) => (
              <li key={i} className="docs-needed-item">
                <Check size={13} className="doc-check" />
                {doc}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/* ─── STEP 3 — PERSONAL ─────────────────────────────────────────────────────── */
function Step3({ data, onChange, errors }) {
  const upd = (k, v) => onChange({ personal: { ...data.personal, [k]: v } });

  return (
    <section className="step-panel">
      <header className="panel-header">
        <span className="panel-step-badge">Step 3 of 6</span>
        <h2 className="panel-title">Personal Information</h2>
        <p className="panel-subtitle">Enter details exactly as they appear on your passport</p>
      </header>

      <div className="form-grid">
        <Field label="First Name" req error={errors?.firstName}>
          <input className={`form-input ${errors?.firstName ? "has-error" : ""}`}
            placeholder="As on passport"
            value={data.personal?.firstName || ""}
            onChange={e => upd("firstName", e.target.value)} />
        </Field>

        <Field label="Last Name" req error={errors?.lastName}>
          <input className={`form-input ${errors?.lastName ? "has-error" : ""}`}
            placeholder="As on passport"
            value={data.personal?.lastName || ""}
            onChange={e => upd("lastName", e.target.value)} />
        </Field>

        <Field label="Date of Birth" req error={errors?.dob}>
          <input className={`form-input ${errors?.dob ? "has-error" : ""}`}
            type="date"
            value={data.personal?.dob || ""}
            onChange={e => upd("dob", e.target.value)} />
        </Field>

        <Field label="Gender" req error={errors?.gender}>
          <select className={`form-input form-select ${errors?.gender ? "has-error" : ""}`}
            value={data.personal?.gender || ""}
            onChange={e => upd("gender", e.target.value)}>
            <option value="">Select gender</option>
            <option>Male</option><option>Female</option><option>Other</option>
          </select>
        </Field>

        <Field label="Nationality" req>
          <input className="form-input" placeholder="e.g. Indian"
            value={data.personal?.nationality || ""}
            onChange={e => upd("nationality", e.target.value)} />
        </Field>

        <Field label="Marital Status">
          <select className="form-input form-select"
            value={data.personal?.maritalStatus || ""}
            onChange={e => upd("maritalStatus", e.target.value)}>
            <option value="">Select status</option>
            <option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
          </select>
        </Field>

        <Field label="Email Address" req error={errors?.email}>
          <input className={`form-input ${errors?.email ? "has-error" : ""}`}
            type="email" placeholder="your@email.com"
            value={data.personal?.email || ""}
            onChange={e => upd("email", e.target.value)} />
        </Field>

        <Field label="Mobile Number" req error={errors?.phone}>
          <div className="phone-row">
            <span className="phone-prefix">🇮🇳 +91</span>
            <input className={`form-input phone-input ${errors?.phone ? "has-error" : ""}`}
              placeholder="10-digit number"
              maxLength={10}
              value={data.personal?.phone || ""}
              onChange={e => upd("phone", e.target.value.replace(/\D/, ""))} />
          </div>
        </Field>

        <Field label="Current Address" req full error={errors?.address}>
          <textarea className={`form-input form-textarea ${errors?.address ? "has-error" : ""}`}
            rows={3}
            placeholder="House no, Street, City, State, PIN"
            value={data.personal?.address || ""}
            onChange={e => upd("address", e.target.value)} />
        </Field>

        <Field label="Occupation" req error={errors?.occupation}>
          <select className={`form-input form-select ${errors?.occupation ? "has-error" : ""}`}
            value={data.personal?.occupation || ""}
            onChange={e => upd("occupation", e.target.value)}>
            <option value="">Select occupation</option>
            <option>Salaried Employee</option>
            <option>Self-Employed / Business</option>
            <option>Student</option><option>Retired</option>
            <option>Homemaker</option><option>Government Employee</option><option>Freelancer</option>
          </select>
        </Field>

        <Field label="Annual Income (₹)">
          <input className="form-input" placeholder="e.g. 1200000"
            value={data.personal?.income || ""}
            onChange={e => upd("income", e.target.value)} />
        </Field>
      </div>
    </section>
  );
}

/* ─── STEP 4 — TRAVEL ───────────────────────────────────────────────────────── */
function Step4({ data, onChange, errors }) {
  const upd = (k, v) => onChange({ travel: { ...data.travel, [k]: v } });

  return (
    <section className="step-panel">
      <header className="panel-header">
        <span className="panel-step-badge">Step 4 of 6</span>
        <h2 className="panel-title">Travel Details</h2>
        <p className="panel-subtitle">Passport information and your travel itinerary</p>
      </header>

      <div className="form-section">
        <h3 className="form-section-title"><FileText size={15} /> Passport Information</h3>
        <div className="form-grid">
          <Field label="Passport Number" req error={errors?.passportNo}>
            <input className={`form-input ${errors?.passportNo ? "has-error" : ""}`}
              placeholder="e.g. A1234567"
              value={data.travel?.passportNo || ""}
              onChange={e => upd("passportNo", e.target.value.toUpperCase())} />
          </Field>

          <Field label="Issue Date" req>
            <input className="form-input" type="date"
              value={data.travel?.passportIssue || ""}
              onChange={e => upd("passportIssue", e.target.value)} />
          </Field>

          <Field label="Expiry Date" req error={errors?.passportExpiry}>
            <input className={`form-input ${errors?.passportExpiry ? "has-error" : ""}`}
              type="date"
              value={data.travel?.passportExpiry || ""}
              onChange={e => upd("passportExpiry", e.target.value)} />
          </Field>

          <Field label="Place of Issue">
            <input className="form-input" placeholder="e.g. Mumbai"
              value={data.travel?.passportPlace || ""}
              onChange={e => upd("passportPlace", e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="form-section">
        <h3 className="form-section-title"><Plane size={15} /> Journey Details</h3>
        <div className="form-grid">
          <Field label="Arrival Date" req error={errors?.arrivalDate}>
            <input className={`form-input ${errors?.arrivalDate ? "has-error" : ""}`}
              type="date"
              value={data.travel?.arrivalDate || ""}
              onChange={e => upd("arrivalDate", e.target.value)} />
          </Field>

          <Field label="Departure Date" req error={errors?.departureDate}>
            <input className={`form-input ${errors?.departureDate ? "has-error" : ""}`}
              type="date"
              value={data.travel?.departureDate || ""}
              onChange={e => upd("departureDate", e.target.value)} />
          </Field>

          <Field label="City of Stay">
            <input className="form-input" placeholder="e.g. Dubai, Singapore"
              value={data.travel?.cityOfStay || ""}
              onChange={e => upd("cityOfStay", e.target.value)} />
          </Field>

          <Field label="Number of Applicants">
            <div className="counter-row">
              <button type="button" className="counter-btn"
                onClick={() => upd("applicants", Math.max(1, (data.travel?.applicants || 1) - 1))}>
                <span>−</span>
              </button>
              <span className="counter-value">{data.travel?.applicants || 1}</span>
              <button type="button" className="counter-btn"
                onClick={() => upd("applicants", Math.min(10, (data.travel?.applicants || 1) + 1))}>
                <span>+</span>
              </button>
            </div>
          </Field>

          <Field label="Accommodation Address" full>
            <textarea className="form-input form-textarea" rows={2}
              placeholder="Hotel name and full address"
              value={data.travel?.hotelAddress || ""}
              onChange={e => upd("hotelAddress", e.target.value)} />
          </Field>

          <Field label="Visited this country before?" full>
            <div className="radio-group">
              {["Yes", "No"].map(opt => (
                <label key={opt} className={`radio-pill ${data.travel?.visitedBefore === opt ? "active" : ""}`}>
                  <input type="radio" name="visited" value={opt}
                    checked={data.travel?.visitedBefore === opt}
                    onChange={() => upd("visitedBefore", opt)} />
                  {opt}
                </label>
              ))}
            </div>
          </Field>

          <Field label="Ever been denied a visa?" full>
            <div className="radio-group">
              {["Yes", "No"].map(opt => (
                <label key={opt} className={`radio-pill ${data.travel?.deniedVisa === opt ? "active" : ""}`}>
                  <input type="radio" name="denied" value={opt}
                    checked={data.travel?.deniedVisa === opt}
                    onChange={() => upd("deniedVisa", opt)} />
                  {opt}
                </label>
              ))}
            </div>
            {data.travel?.deniedVisa === "Yes" && (
              <textarea className="form-input form-textarea" style={{ marginTop: 12 }} rows={2}
                placeholder="Provide details of the denial…"
                value={data.travel?.denialDetails || ""}
                onChange={e => upd("denialDetails", e.target.value)} />
            )}
          </Field>
        </div>
      </div>
    </section>
  );
}

/* ─── STEP 5 — DOCUMENTS ────────────────────────────────────────────────────── */
function Step5({ data, onChange, applicationId, token }) {
  const [uploads,   setUploads]   = useState(data.documents || {});
  const [uploading, setUploading] = useState({});
  const refs = useRef({});
  const docs  = DOCS_REQUIRED[data.visaType] || [];
  const count = Object.keys(uploads).length;
  const pct   = docs.length ? Math.round((count / docs.length) * 100) : 0;

  const handle = async (doc, file) => {
    setUploading(u => ({ ...u, [doc]: true }));
    const u = { ...uploads, [doc]: file };
    setUploads(u);
    onChange({ documents: u });
    if (applicationId) await uploadDocumentToDB(applicationId, doc, file, token);
    setUploading(u => ({ ...u, [doc]: false }));
  };

  const remove = (doc) => {
    const u = { ...uploads };
    delete u[doc];
    setUploads(u);
    onChange({ documents: u });
  };

  return (
    <section className="step-panel">
      <header className="panel-header">
        <span className="panel-step-badge">Step 5 of 6</span>
        <h2 className="panel-title">Upload Documents</h2>
        <p className="panel-subtitle">Clear scans or photos — PDF, JPG, or PNG · Max 5 MB each</p>
      </header>

      <div className="upload-summary">
        <div className="upload-summary-text">
          <span className="upload-count"><strong>{count}</strong> of <strong>{docs.length}</strong> uploaded</span>
          <span className="upload-pct">{pct}%</span>
        </div>
        <div className="upload-bar">
          <div className="upload-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="doc-list">
        {docs.map((doc, i) => {
          const up   = uploads[doc];
          const busy = uploading[doc];
          return (
            <div key={i} className={`doc-row ${up ? "uploaded" : ""}`}>
              <div className="doc-num">{up ? <Check size={13} strokeWidth={2.5} /> : i + 1}</div>
              <div className="doc-info">
                <span className="doc-name">{doc}</span>
                {up
                  ? <span className="doc-file-name"><FileText size={11} /> {up.name}</span>
                  : <span className="doc-hint">PDF · JPG · PNG · Max 5 MB</span>}
              </div>
              <div className="doc-action">
                {busy ? (
                  <span className="doc-uploading">Uploading…</span>
                ) : up ? (
                  <button type="button" className="doc-remove" onClick={() => remove(doc)}>
                    <X size={13} /> Remove
                  </button>
                ) : (
                  <button type="button" className="doc-upload-btn"
                    onClick={() => refs.current[doc]?.click()}>
                    <Upload size={13} /> Upload
                  </button>
                )}
                <input
                  ref={el => refs.current[doc] = el}
                  type="file" accept=".pdf,.jpg,.jpeg,.png"
                  style={{ display: "none" }}
                  onChange={e => e.target.files[0] && handle(doc, e.target.files[0])}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="info-callout">
        <AlertCircle size={16} className="callout-icon" />
        <p>Ensure all documents are legible and match the information provided. Blurry or cropped scans may cause processing delays.</p>
      </div>
    </section>
  );
}

/* ─── STEP 6 — REVIEW ───────────────────────────────────────────────────────── */
function Step6({ data, onEdit }) {
  const applicants = data.travel?.applicants || 1;
  const base    = data.country?.fee || 0;
  const service = 799;
  const total   = (base * applicants) + service;

  const [agreed, setAgreed] = useState(false);

  const ReviewBlock = ({ title, step, rows, icon }) => (
    <div className="review-block">
      <div className="review-block-head">
        <span className="review-block-title">{icon}{title}</span>
        <button type="button" className="review-edit-btn" onClick={() => onEdit(step)}>
          Edit <ArrowRight size={12} />
        </button>
      </div>
      <div className="review-rows">
        {rows.filter(([, v]) => v).map(([l, v]) => (
          <div key={l} className="review-row">
            <span className="review-label">{l}</span>
            <span className="review-value">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section className="step-panel">
      <header className="panel-header">
        <span className="panel-step-badge">Step 6 of 6</span>
        <h2 className="panel-title">Review & Submit</h2>
        <p className="panel-subtitle">Carefully verify all information before final submission</p>
      </header>

      <ReviewBlock title="Destination & Visa" step={1} icon={<Globe size={14} />} rows={[
        ["Country",         `${data.country?.flag} ${data.country?.name}`],
        ["Visa Type",       data.visaType],
        ["Processing Time", data.country?.processingTime],
        ["Visa Format",     data.country?.type],
      ]} />

      <ReviewBlock title="Personal Information" step={3} icon={<User size={14} />} rows={[
        ["Full Name",    `${data.personal?.firstName || ""} ${data.personal?.lastName || ""}`.trim()],
        ["Date of Birth", data.personal?.dob],
        ["Gender",        data.personal?.gender],
        ["Nationality",   data.personal?.nationality],
        ["Email",         data.personal?.email],
        ["Mobile",        data.personal?.phone ? `+91 ${data.personal.phone}` : ""],
        ["Occupation",    data.personal?.occupation],
      ]} />

      <ReviewBlock title="Travel Details" step={4} icon={<Plane size={14} />} rows={[
        ["Passport No.",    data.travel?.passportNo],
        ["Passport Expiry", data.travel?.passportExpiry],
        ["Arrival Date",    data.travel?.arrivalDate],
        ["Departure Date",  data.travel?.departureDate],
        ["Applicants",      data.travel?.applicants],
        ["City of Stay",    data.travel?.cityOfStay],
      ]} />

      <div className="fee-summary">
        <h3 className="fee-title">Fee Summary</h3>
        <div className="fee-rows">
          <div className="fee-row">
            <span>Visa fee — {data.country?.name}</span>
            <span>{fmt(base)} × {applicants}</span>
          </div>
          <div className="fee-row">
            <span>Service charge</span>
            <span>{fmt(service)}</span>
          </div>
        </div>
        <div className="fee-total-row">
          <span>Total payable</span>
          <strong>{fmt(total)}</strong>
        </div>
        <p className="fee-note">All fees inclusive of taxes. Visa fees are non-refundable post submission.</p>
      </div>

      <label className={`consent-block ${agreed ? "agreed" : ""}`}>
        <span className={`consent-checkbox ${agreed ? "checked" : ""}`} onClick={() => setAgreed(a => !a)}>
          {agreed && <Check size={12} strokeWidth={3} />}
        </span>
        <span className="consent-text">
          I confirm all information provided is accurate and complete. I understand that providing false
          information may result in visa rejection or legal action.
        </span>
      </label>
    </section>
  );
}

/* ─── SUCCESS ───────────────────────────────────────────────────────────────── */
function Success({ data, refNumber }) {
  return (
    <div className="success-container">
      <div className="success-icon-ring">
        <Check size={32} strokeWidth={2} />
      </div>
      <h1 className="success-title">Application Submitted!</h1>
      <p className="success-message">
        Your visa application for <strong>{data.country?.flag} {data.country?.name}</strong> has been
        received and is now being processed.
      </p>

      <div className="success-ref-card">
        <span className="success-ref-label">Reference Number</span>
        <span className="success-ref-code">{refNumber}</span>
      </div>

      <div className="timeline">
        {[
          { label: "Application Received",  detail: "Just now",                   done: true  },
          { label: "Document Verification", detail: "1–2 business days",          done: false },
          { label: "Embassy Processing",    detail: data.country?.processingTime, done: false },
          { label: "Visa Decision",         detail: "Via email & SMS",            done: false },
        ].map((item, i) => (
          <div key={i} className={`timeline-item ${item.done ? "done" : ""}`}>
            <div className="timeline-dot">{item.done ? <Check size={10} strokeWidth={3} /> : ""}</div>
            <div className="timeline-content">
              <strong>{item.label}</strong>
              <span>{item.detail}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="success-actions">
        <button type="button" className="action-btn primary">
          <FileText size={16} /> Download Receipt
        </button>
        <button type="button" className="action-btn" onClick={() => window.location.reload()}>
          Apply for Another Visa
        </button>
      </div>
    </div>
  );
}

/* ─── SIDEBAR ───────────────────────────────────────────────────────────────── */
function Sidebar({ data, saveStatus }) {
  const base       = data.country?.fee || 0;
  const applicants = data.travel?.applicants || 1;
  const total      = (base * applicants) + 799;

  const perks = [
    { icon: <Sparkles size={15} />,       text: "99.2% approval rate"           },
    { icon: <Zap size={15} />,            text: "Express processing available"  },
    { icon: <ShieldCheck size={15} />,    text: "100% secure & encrypted"       },
    { icon: <Headset size={15} />,        text: "24×7 visa expert support"      },
    { icon: <BadgeDollarSign size={15} />,text: "Money-back guarantee"          },
  ];

  return (
    <aside className="visa-sidebar">
      {saveStatus && (
        <div className="visa-sidebar-save">
          <SaveIndicator status={saveStatus} />
        </div>
      )}

      {data.country && (
        <div className="visa-sidebar-card">
          <span className="visa-sidebar-card-label">Your Selection</span>
          <div className="visa-sidebar-dest">
            <span className="visa-sidebar-dest-flag">{data.country.flag}</span>
            <div>
              <p className="visa-sidebar-dest-name">{data.country.name}</p>
              <p className="visa-sidebar-dest-type">{data.country.type}</p>
            </div>
          </div>
          {data.visaType && (
            <div className="visa-sidebar-purpose">
              <FileText size={12} /> {data.visaType} Visa
            </div>
          )}
          <div className="visa-sidebar-fee-rows">
            <div className="sfr"><span>Processing time</span><span>{data.country.processingTime}</span></div>
            <div className="sfr"><span>Visa fee</span><span>{fmt(base)}</span></div>
            <div className="sfr"><span>Service charge</span><span>₹799</span></div>
            <div className="sfr total"><span>Total</span><span>{fmt(total)}</span></div>
          </div>
        </div>
      )}

      <div className="visa-sidebar-card">
        <span className="visa-sidebar-card-label">Why MakeBeeTrip?</span>
        <ul className="perk-list">
          {perks.map(({ icon, text }) => (
            <li key={text} className="perk-item">
              <span className="perk-icon">{icon}</span>
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="visa-sidebar-help">
        <div className="help-icon"><Headset size={22} /></div>
        <div>
          <p className="help-label">Need assistance?</p>
          <p className="help-number">1800-123-4567</p>
          <p className="help-hours">Mon–Sat · 9am–9pm IST</p>
        </div>
      </div>
    </aside>
  );
}

/* ─── FIELD ─────────────────────────────────────────────────────────────────── */
function Field({ label, req, error, full, children }) {
  return (
    <div className={`field-group ${full ? "full-width" : ""}`}>
      <label className="field-label">
        {label}
        {req && <span className="field-required">*</span>}
      </label>
      {children}
      {error && <span className="field-error"><AlertCircle size={11} /> {error}</span>}
    </div>
  );
}

/* ─── VALIDATION ────────────────────────────────────────────────────────────── */
function validate(step, data) {
  const e = {};
  if (step === 1 && !data.country)   return { country:  "Please select a destination" };
  if (step === 2 && !data.visaType)  return { visaType: "Please select a visa type"   };
  if (step === 3) {
    if (!data.personal?.firstName)                                            e.firstName  = "Required";
    if (!data.personal?.lastName)                                             e.lastName   = "Required";
    if (!data.personal?.dob)                                                  e.dob        = "Required";
    if (!data.personal?.gender)                                               e.gender     = "Required";
    if (!data.personal?.email || !/\S+@\S+\.\S+/.test(data.personal.email))  e.email      = "Valid email required";
    if (!data.personal?.phone || data.personal.phone.length !== 10)          e.phone      = "Valid 10-digit number required";
    if (!data.personal?.address)                                              e.address    = "Required";
    if (!data.personal?.occupation)                                           e.occupation = "Required";
  }
  if (step === 4) {
    if (!data.travel?.passportNo)     e.passportNo     = "Required";
    if (!data.travel?.passportExpiry) e.passportExpiry = "Required";
    if (!data.travel?.arrivalDate)    e.arrivalDate    = "Required";
    if (!data.travel?.departureDate)  e.departureDate  = "Required";
  }
  return e;
}

/* ─── HERO ──────────────────────────────────────────────────────────────────── */
function Hero({ onDestinationSearch }) {
  const [heroSearch, setHeroSearch] = useState("");
const [destination, setDestination] = useState("");
const [nationality, setNationality] = useState("Indian");
const [visaType, setVisaType] = useState("Tourist");
const [travelDate, setTravelDate] = useState("");
const [heroError, setHeroError] = useState("");

  const submitHeroSearch = (event) => {
  event.preventDefault();

  if (!destination) {
    setHeroError("Please select your destination country.");
    return;
  }

  if (!nationality) {
    setHeroError("Please select your nationality.");
    return;
  }

  if (!visaType) {
    setHeroError("Please select a visa type.");
    return;
  }

  if (!travelDate) {
    setHeroError("Please select your travel date.");
    return;
  }

  setHeroError("");

  onDestinationSearch?.(destination);
};

  return (
    <header className="visa-hero">
      <div className="visa-hero-shade" />
      <div className="visa-hero-shell">
        <div className="visa-hero-copy" data-aos="fade-right">
          <span className="visa-trust-pill"><ShieldCheck size={14} /> Visa Assistance Made Simple</span>
          <h1 className="visa-hero-title">
            Plan Your Visa <span>With Expert Assistance</span>
          </h1>
          <p className="visa-hero-text">
            Explore Tourist, Business, Student & Work Visa options with expert guidance and secure enquiry support.
          </p>
          <div className="visa-hero-benefits" aria-label="Visa service benefits">
            <span><ShieldCheck size={14} /> Multiple Destinations</span>
            <span><Clock size={14} /> Visa Assistance</span>
            <span><Lock size={14} /> Secure Enquiry</span>
            <span><Zap size={14} /> Expert Guidance</span>
          </div>
        </div>

        <div className="visa-hero-visual" data-aos="fade-left" data-aos-delay="100" aria-hidden="true">
          <span className="visa-plane-mark"><Plane size={22} /></span>
          <div className="visa-passport-card">
            <div className="passport-cover">
              <span>PASSPORT</span>
              <Globe size={44} />
            </div>
            <div className="visa-paper">
              <strong>VISA</strong>
              <em><Check size={13} /> ASSISTANCE</em>
            </div>
          </div>
          <div className="visa-approval-card">
            <small>VISA SUPPORT</small>
            <strong>Expert</strong>
            <span>Guidance</span>
          </div>
        </div>

        <form className="visa-hero-search-panel" onSubmit={submitHeroSearch}>
          <label>
            <span>Destination Country</span>
            <select value={destination} onChange={(event) => setDestination(event.target.value)}>
              <option value="">Select Country</option>
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.name}>{country.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Nationality</span>
            <select value={nationality} onChange={(event) => setNationality(event.target.value)}>
              <option>Indian</option>
              <option>United States</option>
              <option>United Kingdom</option>
              <option>Canada</option>
            </select>
          </label>
          <label>
            <span>Visa Type</span>
            <select value={visaType} onChange={(event) => setVisaType(event.target.value)}>
              {VISA_TYPES.map(type => <option key={type}>{type}</option>)}
            </select>
          </label>
          <label>
            <span>Travel Date</span>
            <input type="date" value={travelDate} onChange={(event) => setTravelDate(event.target.value)} />
          </label>
          <button className="visa-check-button" type="submit">
            Explore Visa Options <Search size={15} />
          </button>
          <div className="visa-hero-assurance">
            <span><Sparkles size={13} /> Simple Enquiry Process</span>
            <span><Clock size={13} /> Multiple Destinations</span>
            <span><ShieldCheck size={13} /> Secure Enquiry</span>
            <span><Headset size={13} /> Expert Support</span>
          </div>
          {heroError && (
  <p className="visa-hero-form-error" role="alert">
    {heroError}
  </p>
)}
        </form>

      </div>
    </header>
  );
}

/* ─── MAIN PAGE ─────────────────────────────────────────────────────────────── */
function TrendingAttractions({ onSelect }) {
  return (
    <section className="trending-attractions" aria-labelledby="trending-attractions-title">
      <div className="trending-attractions-inner">
        <p className="trending-kicker">Explore Destinations</p>
        <h2 className="trending-title" id="trending-attractions-title">Popular Visa Destinations</h2>
        <div className="trending-card-grid">
          {TRENDING_DESTINATIONS.map((item, index) => (
            <article
              className="trending-card mbt-animated-card"
              key={item.country}
              data-aos="fade-up"
              data-aos-delay={index * 70}
              role="button"
              tabIndex={0}
              onClick={() => onSelect?.(item.code)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                onSelect?.(item.code);
              }}
            >
              <img className="trending-card-image" src={item.image} alt={`${item.country} tourist attraction`} />
              <div className="trending-card-info">
  <div className="trending-card-place">
    <MapPin size={14} strokeWidth={2.3} />
    <strong>{item.country}</strong>
  </div>
  <p>Explore Visa Options <ArrowRight size={13} /></p>
</div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="visa-how" aria-labelledby="visa-how-title">
      <div className="visa-how-inner">

        {/* Header */}
        <div className="visa-how-heading">
          <span className="visa-how-eyebrow">
            <span className="visa-how-eyebrow-line" />
            How It Works
          </span>

          <h2 id="visa-how-title">
            Get Started in 4 Simple Steps
          </h2>

          <p>
            Our visa enquiry process is simple and straightforward. Choose
            your destination, share your travel details and let our visa
            assistance team guide you through the next steps.
          </p>
        </div>

        {/* Steps */}
        <div className="visa-how-flow">
          {HOW_IT_WORKS.map((item, index) => {
            const Icon = item.icon;

            return (
              <article
                className="visa-how-card"
                key={item.step}
                data-aos="fade-up"
                data-aos-delay={index * 80}
              >
                <div className="visa-how-card-top">
  <div className="visa-how-icon">
    <Icon
      size={22}
      strokeWidth={1.8}
      aria-hidden="true"
    />
  </div>

  <span className="visa-how-step">
    {item.step}
  </span>
</div>

                <div className="visa-how-card-content">
                  <h3>{item.title}</h3>

                  <p>{item.text}</p>
                </div>
              </article>
            );
          })}
        </div>

        {/* Guidance Note */}
        <div className="visa-how-note">
          <div className="visa-how-note-icon">
            <ShieldCheck size={18} strokeWidth={1.8} />
          </div>

          <div>
            <strong>
              Clear guidance throughout your enquiry
            </strong>

            <p>
              Visa requirements may vary depending on your destination,
              nationality and purpose of travel. Our team will guide you
              based on the information available for your enquiry.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}

function KnownFor() {
  return (
    <section className="visa-known" aria-labelledby="visa-known-title">
      <div className="visa-known-inner">

        <div className="visa-known-heading">
          <p className="visa-known-kicker">Why Choose Us</p>

          <h2
            className="visa-known-title"
            id="visa-known-title"
          >
            Why Choose Our Visa Assistance?
          </h2>
        </div>

        <div className="visa-known-grid">
          {KNOWN_FOR.map((item, index) => (
            <article
              className="visa-known-card"
              key={item.title}
              data-aos="fade-up"
              data-aos-delay={index * 70}
            >
              <span className="visa-known-number">
                {String(index + 1).padStart(2, "0")}
              </span>

              <div className="visa-known-card-content">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </article>
          ))}
        </div>

      </div>
    </section>
  );
}

export default function VisaPage() {
  usePageMeta(
  "Visa Services Online | Tourist Visa Assistance – MakeBeeTrip",
  "Apply for tourist visas with expert assistance on MakeBeeTrip. Check visa requirements, documents and fees, and track your visa application end to end.",
  "visa services, tourist visa, visa application online, visa assistance, visa requirements, dubai visa, singapore visa, malaysia visa, japan visa, schengen visa"
);
  const navigate = useNavigate();
  const { user, token, authLoading } = useAuth();
  const [step,          setStep]          = useState(1);
  const [submitted,     setSubmitted]     = useState(false);
  const [refNumber,     setRefNumber]     = useState("");
  const [form,          setForm]          = useState({ personal: {}, travel: { applicants: 1 }, documents: {} });
  const [errors,        setErrors]        = useState({});
  const [applicationId, setApplicationId] = useState(null);
  const [saveStatus,    setSaveStatus]    = useState(null);
  const [loginOpen,     setLoginOpen]     = useState(false);
  const [signupOpen,    setSignupOpen]    = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 650, once: true, easing: "ease-out-cubic", offset: 50 });
    const timer = setTimeout(() => AOS.refresh(), 80);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  useEffect(() => {
    if (!user) return;
    setForm(cur => ({
      ...cur,
      personal: {
        ...cur.personal,
        email: cur.personal.email || user.email || "",
        phone: cur.personal.phone || user.phone || "",
      },
    }));
  }, [user]);

  const saveTimerRef = useRef(null);

  const triggerAutoSave = useCallback(async (updatedForm, currentStep) => {
    if (!token) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSaveStatus("saving");
      let result;
      if (!applicationId) {
        result = await saveApplicationToDB(updatedForm, currentStep, token);
        if (result.success && result.applicationId) setApplicationId(result.applicationId);
      } else {
        result = await updateApplicationInDB(applicationId, updatedForm, currentStep, token);
      }
      setSaveStatus(result.success ? "saved" : "error");
      setTimeout(() => setSaveStatus(null), 3000);
    }, 1200);
  }, [applicationId, token]);

  const update = useCallback((patch) => {
    setForm(f => {
      const updated = { ...f, ...patch };
      triggerAutoSave(updated, step);
      return updated;
    });
  }, [step, triggerAutoSave]);

  const handleHeroDestinationSearch = useCallback((term) => {
  const destination = COUNTRIES.find(
    (country) =>
      country.name.toLowerCase() === term.toLowerCase() ||
      country.code.toLowerCase() === term.toLowerCase()
  );

  if (!destination) return;

  navigate(`/visa/type/${destination.code.toLowerCase()}`);
}, [navigate]);

  const openVisaTypePage = useCallback((countryOrCode) => {
    const code = typeof countryOrCode === "string" ? countryOrCode : countryOrCode?.code;
    if (!code) return;
    navigate(`/visa/type/${code.toLowerCase()}`);
  }, [navigate]);

  const next = async () => {
    const e = validate(step, form);
    if (Object.keys(e).length) { setErrors(e); return; }
    if (!token) { setLoginOpen(true); return; }
    setErrors({});

    setSaveStatus("saving");
    let result;
    let currentApplicationId = applicationId;
    if (!applicationId) {
      result = await saveApplicationToDB(form, step, token);
      if (result.success && result.applicationId) {
        currentApplicationId = result.applicationId;
        setApplicationId(currentApplicationId);
      }
    } else {
      result = await updateApplicationInDB(applicationId, form, step, token);
    }
    setSaveStatus(result.success ? "saved" : "error");
    setTimeout(() => setSaveStatus(null), 2000);
    if (!result.success) return;

    if (step < STEPS.length) {
      setStep(s => s + 1);
    } else {
      setSaveStatus("saving");
      const submitResult = await submitApplicationToDB(currentApplicationId, form, token);
      setSaveStatus(submitResult.success ? "saved" : "error");
      if (submitResult.success) {
        setRefNumber(submitResult.referenceNumber);
        setSubmitted(true);
      }
    }
  };

  const back = () => { setStep(s => s - 1); setErrors({}); };

  if (authLoading) {
    return (
      <div className="visa-page" ref={topRef}>
        <Hero onDestinationSearch={handleHeroDestinationSearch} />
        <TrendingAttractions onSelect={openVisaTypePage} />
        <HowItWorks />
        <KnownFor />
        <div className="page-body page-body--single">
          <div className="auth-card">
            <div className="auth-spinner" />
            <p>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user || !token) {
    return (
      <div className="visa-page" ref={topRef}>
        <Hero onDestinationSearch={handleHeroDestinationSearch} />
        <TrendingAttractions onSelect={openVisaTypePage} />
        <HowItWorks />
        <KnownFor />
        <div className="page-body page-body--single">
          <div className="auth-card">
            <div className="auth-icon-ring">
              <Lock size={24} />
            </div>
            <h2 className="auth-title">Sign in to Continue</h2>
            <p className="auth-sub">Create an account or log in to start your visa application.</p>
            <div className="auth-actions">
              <button type="button" className="action-btn primary" onClick={() => setLoginOpen(true)}>
                Log in <ArrowRight size={15} />
              </button>
              <button type="button" className="action-btn" onClick={() => setSignupOpen(true)}>
                Create account
              </button>
            </div>
          </div>
        </div>
        {loginOpen  && <Login  onClose={() => setLoginOpen(false)}  onSwitchToSignup={() => { setLoginOpen(false);  setSignupOpen(true); }} />}
        {signupOpen && <Signup onClose={() => setSignupOpen(false)} onSwitchToLogin={() => { setSignupOpen(false); setLoginOpen(true); }} />}
      </div>
    );
  }

  if (submitted) return (
    <div className="visa-page" ref={topRef}>
      <Hero onDestinationSearch={handleHeroDestinationSearch} />
      <TrendingAttractions onSelect={openVisaTypePage} />
      <HowItWorks />
      <KnownFor />
      <div className="page-body page-body--single">
        <Success data={form} refNumber={refNumber} />
      </div>
    </div>
  );

  return (
    <div className="visa-page" ref={topRef}>
      <Hero onDestinationSearch={handleHeroDestinationSearch} />
      <TrendingAttractions onSelect={openVisaTypePage} />
      <HowItWorks />
      <KnownFor />
    </div>
  );
}
